import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import React, {useState} from 'react';
import axios from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {useFormik} from 'formik';
import * as yup from 'yup';
import Button from '@/components/ui/Button';
import {useStoreActions} from 'easy-peasy';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';
import {buildStoryboardURL} from '@/utils/urlBuilder';
import {CellLoader} from '@/components/ui';
import {extractErrorMessage} from '@/utils/errorHandling';

interface User {
    id: number;
    identifier: string;
    email: string;
}

interface UserOption {
    label: string;
    value: number | string;
}

interface AccessTypeOption {
    label: string;
    value: string;
}

interface FormValues {
    users: UserOption[];
    accessType: AccessTypeOption;
}

interface StoryboardSharingFormProps {
    storyboardId: number;
    onFinish: () => void;
}

const loadStoryboardUsersRequest = (inputValue: string) => axios.get<{ data: User[] }>(window.URLS.userSearch, {
    params: {
        query: inputValue,
    },
})
    .catch((error) => {
        if (error.response.status === 400) {
            return error.response;
        }

        throw error;
    });
const loadStoryboardUsersRequestDebounced = AwesomeDebouncePromise(loadStoryboardUsersRequest, 280);

const StoryboardSharingFormSchema = yup.object({
    users: yup.array().of(
        yup.object({
            value: yup.number().required(),
            label: yup.string().required(),
        }).required(),
    ).required(i18n.t('jt_st_required_user_selection.text')),
    accessType: yup.object({
        value: yup.string().required(),
        label: yup.string().required(),
    }).required(),
});

const StoryboardSharingForm: React.FC<StoryboardSharingFormProps> = React.memo((
    {
        storyboardId,
        onFinish,
    },
) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const editStoryboardAccess = useStoreActions((actions: any) => actions.storyboards.editStoryboardAccess);
    const {showError, showSuccess} = useAlert();
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);

    const currentUserId = userData?.id;

    const onSubmit = (values: FormValues) => {
        axios.post(buildStoryboardURL('shareStoryboard', storyboardId), values)
            .then((response) => {
                formik.setSubmitting(false);
                editStoryboardAccess([storyboardId, response.data.relatedUsers]);
                showSuccess(i18n.t('story_shared_successfully.text'));
                onFinish();
            })
            .catch((error) => {
                formik.setSubmitting(false);
                console.error('Failed to share storyboard', error);

                const errorMessage = extractErrorMessage(error, i18n.t('ch_something_went_wrong.text'));
                formik.setFieldError('users', errorMessage);
                showError(errorMessage);
            });
    };

    const formik = useFormik<FormValues>({
        validationSchema: StoryboardSharingFormSchema,
        initialValues: {
            users: [],
            accessType: {
                label: i18n.t('jt_access_view.text'),
                value: 'read',
            },
        },
        onSubmit,
    });

    // Early return if userData is not loaded yet - AFTER all hooks are defined
    if (loadingUserData || !userData) {
        return <CellLoader fullHeight={true} />;
    }

    const getUserOptions = async (inputValue: string): Promise<UserOption[]> => {
        const results = await loadStoryboardUsersRequestDebounced(inputValue);

        const selectAllOption: UserOption = {
            label: i18n.t('jt_st_select_all.text'),
            value: '*',
        };

        const userChoices: UserOption[] = results.data.filter((user: User) => user.id !== currentUserId).map((user: User) => ({
            label: `${user.identifier} (${user.email})`,
            value: user.id,
        }));
        setUserOptions(userChoices);

        return [
            ...(inputValue || !userChoices.length ? [] : [selectAllOption]),
            ...userChoices,
        ];
    };

    const onUserSelection = (value: readonly UserOption[] | null) => {
        if (value && value.length && value[value.length - 1].value === '*') {
            formik.setFieldValue('users', userOptions);
        } else {
            formik.setFieldValue('users', value ? [...value] : []);
        }
    };

    return (
        <Form noValidate onSubmit={formik.handleSubmit}>
            <div className="form-row">
                <Form.Group className="mb-3" controlId="users" as={Col} xs="6" >
                    <AsyncSelect
                        isMulti
                        cacheOptions
                        defaultOptions
                        loadOptions={getUserOptions}
                        hideSelectedOptions={false}
                        value={formik.values.users}
                        onChange={onUserSelection}
                        styles={{
                            valueContainer: (provided) => ({
                                ...provided,
                                maxHeight: '100px',
                                overflowY: 'scroll',
                                scrollbarWidth: 'none',
                                overflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    width: 0,
                                    height: 0,
                                },
                            }),
                        }}
                    />
                    <Form.Control.Feedback
                        type="invalid"
                        style={{display: formik.errors.users ? 'block' : 'none'}}
                    >
                        {typeof formik.errors.users === 'string' ? formik.errors.users : ''}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="userAccessType" as={Col} xs="4" >
                    <Select
                        defaultValue={{
                            label: i18n.t('jt_access_view.text'),
                            value: 'read',
                        }}
                        options={[
                            {
                                label: i18n.t('jt_access_view.text'),
                                value: 'read',
                            },
                            {
                                label: i18n.t('jt_access_edit_and_delete.text'),
                                value: 'write',
                            },
                        ]}
                        onChange={(value: AccessTypeOption | null) => formik.setFieldValue('accessType', value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="storyboardTitle-save" as={Col} xs="2" >
                    <Button
                        variant="info"
                        size="sm"
                        fullWidth
                        type="submit"
                        disabled={formik.isSubmitting}
                        loading={formik.isSubmitting}
                        data-cy="storyboard-share-btn"
                    >
                        {i18n.t('jt_st_share.text')}
                    </Button>
                </Form.Group >
            </div >
        </Form >
    );
});


export default StoryboardSharingForm; 